import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { combineLatest, of } from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators'
import { getCurrentLocale } from '../../../shared/i18n/i18n.utils'
import { ErrorReportingService } from '../../../shared/services/error-reporting.service'
import { AAAStore } from '../../../store/root-reducer'
import { selectBreakdownCoordinates, selectLocationClub } from '../../location/location.selectors'
import { selectMemberActiveVehicle } from '../../member/member.selectors'
import { selectEditingStep } from '../../ui/ui.selectors'
import { StepTypes } from '../../ui/ui.types'
import { setIssueOptionSelected } from '../issue.actions'
import { selectIssueOptions, selectIssueOptionsLoadFailed } from '../issue.selectors'
import { findIssueOptionByCode } from '../issue.utils'
import {
  AAA_ASSIST,
  AAA_ASSIST_CONFIRM,
  AAA_ASSIST_RESET,
  AAA_ASSIST_SEND_TURN,
  AAA_ASSIST_START,
  aaaAssistConfirmFailure,
  aaaAssistConfirmSuccess,
  aaaAssistFailure,
  aaaAssistSuccess,
  confirmAaaAssist,
  sendTurn,
  startAaaAssist,
} from './aaa-assist.actions'
import { selectAaaAssistState } from './aaa-assist.selectors'
import { AaaAssistService } from './aaa-assist.service'
import { AaaAssistError, ScenarioSuggestedResponse, toAaaAssistLanguage } from './aaa-assist.types'

// Maps whatever the request threw to the error kept in the store. The timeout interceptor
// surfaces an rxjs TimeoutError, the error interceptor a normalized HTTP error and the
// response validation a yup ValidationError.
export const toAaaAssistError = (error: any): AaaAssistError => {
  if (error?.name === 'TimeoutError') {
    return { errorType: 'TIMEOUT' }
  }
  if (error?.name === 'ValidationError') {
    return { errorType: 'INVALID_RESPONSE' }
  }
  const status = error?.status ?? error?.response?.status
  return { errorType: 'HTTP', ...(status ? { status } : {}) }
}

const MISSING_CONTEXT_ERROR: AaaAssistError = { errorType: 'MISSING_CONTEXT' }

// Both endpoints need the servicing club in the path and the breakdown coordinates in the body.
const hasRequestContext = (context: { clubCode?: string; latitude?: number; longitude?: number }) =>
  !!context.clubCode && Number.isFinite(context.latitude) && Number.isFinite(context.longitude)

@Injectable()
export class AaaAssistEffects {
  constructor(
    private actions$: Actions,
    private errorReportingService: ErrorReportingService,
    private service: AaaAssistService,
    private store$: Store<AAAStore>
  ) {}

  private requestContext$ = () =>
    this.store$.select(selectLocationClub).pipe(
      withLatestFrom(
        this.store$.select(selectBreakdownCoordinates),
        this.store$.select(selectMemberActiveVehicle)
      ),
      map(([clubCode, coordinates, vehicle]) => ({
        clubCode,
        latitude: coordinates?.lat,
        longitude: coordinates?.lng,
        vehicleType: vehicle?.vehicleType ? String(vehicle.vehicleType) : undefined,
        fuelType: vehicle?.fuelType ? String(vehicle.fuelType) : undefined,
      })),
      // Snapshot only: the store streams never complete and must not re-fire a request.
      take(1)
    )

  // A reset abandons the conversation: the request in flight is cancelled so a late response
  // can't refill the slice.
  private reset$ = () => this.actions$.pipe(ofType(AAA_ASSIST_RESET))

  // The first turn sends the member's text as `scenario`; every later turn re-sends the whole
  // transcript. A SEND_TURN the reducer ignored never reaches the API: a confirm in flight also
  // sets `inFlight` but follows a suggested response, and a turn dropped while one is in flight
  // leaves the transcript the previous request already sent.
  triage$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof startAaaAssist> | ReturnType<typeof sendTurn>>(
        AAA_ASSIST_START,
        AAA_ASSIST_SEND_TURN
      ),
      withLatestFrom(this.store$.select(selectAaaAssistState)),
      filter(([, state]) => state.inFlight && state.latestResponse?.status !== 'suggested'),
      distinctUntilChanged(([, previous], [, current]) => previous.messages === current.messages),
      switchMap(([, state]) =>
        this.requestContext$().pipe(
          switchMap(({ clubCode, ...context }) => {
            if (!hasRequestContext({ clubCode, ...context })) {
              return of(aaaAssistFailure({ payload: { error: MISSING_CONTEXT_ERROR } }))
            }
            return this.service
              .triage$({
                clubCode,
                ...context,
                language: toAaaAssistLanguage(getCurrentLocale()?.locale),
                conversationId: state.conversationId ?? undefined,
                ...(state.messages.length === 1
                  ? { scenario: state.messages[0].content }
                  : { messages: state.messages }),
              })
              .pipe(
                takeUntil(this.reset$()),
                map((response) => aaaAssistSuccess({ payload: { response } })),
                catchError((error) =>
                  this.errorReportingService.notifyErrorObservable(error, [
                    aaaAssistFailure({ payload: { error: toAaaAssistError(error) } }),
                  ])
                )
              )
          })
        )
      )
    )
  )

  // A confirm the reducer ignored never reaches the API: it leaves the slice untouched, so it
  // repeats the state the previous confirm already sent, or a triage turn is in flight instead.
  confirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof confirmAaaAssist>>(AAA_ASSIST_CONFIRM.REQUEST),
      withLatestFrom(this.store$.select(selectAaaAssistState)),
      filter(([, state]) => state.inFlight && state.latestResponse?.status === 'suggested'),
      distinctUntilChanged(([, previous], [, current]) => previous === current),
      switchMap(([action, state]) =>
        this.requestContext$().pipe(
          switchMap(({ clubCode, ...context }) => {
            if (!hasRequestContext({ clubCode, ...context })) {
              return of(aaaAssistConfirmFailure({ payload: { error: MISSING_CONTEXT_ERROR } }))
            }
            return this.service
              .confirm$({
                clubCode,
                ...context,
                optionCode: action.payload.optionCode,
                conversationId: state.conversationId ?? undefined,
              })
              .pipe(
                takeUntil(this.reset$()),
                map((result) => aaaAssistConfirmSuccess({ payload: { result } })),
                catchError((error) =>
                  this.errorReportingService.notifyErrorObservable(error, [
                    aaaAssistConfirmFailure({ payload: { error: toAaaAssistError(error) } }),
                  ])
                )
              )
          })
        )
      )
    )
  )

  // club-svc sends no confirmation prompt when it committed on the model's behalf: there is
  // nothing to ask the member, so the suggestion is confirmed straight away (DRRWEB-3741).
  autoConfirmWithoutPrompt$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof aaaAssistSuccess>>(AAA_ASSIST.SUCCESS),
      map((action) => action.payload.response),
      filter(
        (response): response is ScenarioSuggestedResponse =>
          response.status === 'suggested' && !response.confirmationPrompt
      ),
      map((response) => confirmAaaAssist({ payload: { optionCode: response.optionCode } }))
    )
  )

  // The confirmed option is selected like a tile tap: processIssueOptionSelected$ then moves on
  // and opens the same dialogs. Matched by optionCode, not pacesetterCode, which several options
  // share (e.g. L302). A nested option carries its parent's service under its own, as the tiles
  // compose it. An option the Issue page no longer offers is reported and nothing is selected.
  // AAA Assist can be used while the Issue page is still loading its options (they reload whenever
  // the location or vehicle changes), so the match waits for the load to settle. A member who
  // leaves the Issue step meanwhile drops the pick, or it would move them on from another step.
  selectConfirmedIssueOption$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof aaaAssistConfirmSuccess>>(AAA_ASSIST_CONFIRM.SUCCESS),
      switchMap((action) =>
        combineLatest([
          this.store$.select(selectIssueOptions),
          this.store$.select(selectIssueOptionsLoadFailed),
        ]).pipe(
          filter(([issueOptions, loadFailed]) => issueOptions !== null || loadFailed),
          take(1),
          map(([issueOptions]) => [action, issueOptions] as const),
          takeUntil(
            this.store$
              .select(selectEditingStep)
              .pipe(filter((step) => step?.url !== StepTypes.ISSUE))
          )
        )
      ),
      switchMap(([action, issueOptions]) => {
        const { optionCode } = action.payload.result
        const match = findIssueOptionByCode(issueOptions?.options, optionCode)
        if (!match || match.option.enabled === false) {
          return this.errorReportingService.notifyErrorObservable(
            new Error(`AAA Assist confirmed option ${optionCode} is not available on the Issue page`),
            []
          )
        }
        const { option, parent } = match
        return [
          setIssueOptionSelected({
            payload: { option, composedService: { ...parent?.service, ...option.service } },
          }),
        ]
      })
    )
  )

  // The slice is persisted to sessionStorage with the rest of the store, so a reload mid-request
  // rehydrates `inFlight: true` with no request running. Close it out as interrupted.
  clearStaleInFlight$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      withLatestFrom(this.store$.select(selectAaaAssistState)),
      filter(([, state]) => !!state?.inFlight),
      map(() => aaaAssistFailure({ payload: { error: { errorType: 'INTERRUPTED' } } }))
    )
  )
}
